import React, { useState, useCallback, useRef } from 'react';
import FourPartIllustration from '../../../../../../assets/sme/sso/FourPartIllustration.png';
// import ImgPoint from '../../../../../../assets/2025/arrow.gif';
import './index.less';

export interface ISenderVerificationStep {
  id?: string | number;
  step?: number | string;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
}

export interface ISenderVerificationProps {
  title?: string;
  steps?: ISenderVerificationStep[];
  onComplete?: () => void;
  id?: string;
  imgBg?: string;
}

const iconMap: Record<number, string> = {
  1: `<svg width="61" height="58" viewBox="0 0 61 58" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M19.75 25.8333L28.875 34.9583L59.2917 4.54167M56.25 28.875V50.1667C56.25 51.7801 55.6091 53.3274 54.4682 54.4682C53.3274 55.6091 51.7801 56.25 50.1667 56.25H7.58333C5.96993 56.25 4.42261 55.6091 3.28177 54.4682C2.14092 53.3274 1.5 51.7801 1.5 50.1667V7.58333C1.5 5.96993 2.14092 4.42261 3.28177 3.28177C4.42261 2.14092 5.96993 1.5 7.58333 1.5H41.0417" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`,
  2: `<svg width="58" height="56" viewBox="0 0 58 56" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M28.875 53.5774H56.25M42.5625 3.38985C43.7726 2.1798 45.4137 1.5 47.125 1.5C47.9723 1.5 48.8114 1.66689 49.5942 1.99116C50.377 2.31542 51.0883 2.79069 51.6875 3.38985C52.2867 3.98901 52.7619 4.70031 53.0862 5.48314C53.4105 6.26598 53.5774 7.10501 53.5774 7.95235C53.5774 8.79968 53.4105 9.63872 53.0862 10.4216C52.7619 11.2044 52.2867 11.9157 51.6875 12.5148L13.6667 50.5357L1.5 53.5774L4.54167 41.4107L42.5625 3.38985Z" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`,
  3: `<svg width="70" height="52" viewBox="0 0 70 52" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.5 25.8333C1.5 25.8333 13.6667 1.5 34.9583 1.5C56.25 1.5 68.4167 25.8333 68.4167 25.8333C68.4167 25.8333 56.25 50.1667 34.9583 50.1667C13.6667 50.1667 1.5 25.8333 1.5 25.8333Z" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M34.9583 34.9583C39.9979 34.9583 44.0833 30.8729 44.0833 25.8333C44.0833 20.7937 39.9979 16.7083 34.9583 16.7083C29.9187 16.7083 25.8333 20.7937 25.8333 25.8333C25.8333 30.8729 29.9187 34.9583 34.9583 34.9583Z" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`,
  4: `<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M62.3333 31.9167C62.3333 48.7153 48.7153 62.3333 31.9167 62.3333M62.3333 31.9167C62.3333 15.118 48.7153 1.5 31.9167 1.5M62.3333 31.9167H50.1667M31.9167 62.3333C15.118 62.3333 1.5 48.7153 1.5 31.9167M31.9167 62.3333V50.1667M1.5 31.9167C1.5 15.118 15.118 1.5 31.9167 1.5M1.5 31.9167H13.6667M31.9167 1.5V13.6667" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`,
};

const DownArrow = () => (
  <svg
    className="sender-verification-arrow"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 5V19M12 19L5 12M12 19L19 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SenderVerification: React.FunctionComponent<ISenderVerificationProps> = ({
  title,
  steps = [],
  onComplete,
  id,
  imgBg,
}) => {
  const [visibleCount, setVisibleCount] = useState(1);
  const hasCompletedRef = useRef(false);

  const allVisible = visibleCount >= steps.length;

  const handleShowNext = useCallback(() => {
    setVisibleCount((prev) => {
      const next = Math.min(prev + 1, steps.length);
      if (next >= steps.length && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        setTimeout(() => onComplete?.(), 0);
      }
      return next;
    });
  }, [steps.length, onComplete]);

  if (!steps.length) {
    return null;
  }

  const visibleSteps = steps.slice(0, visibleCount);

  return (
    <div className="sender-verification" id={id}>
      {title && <h3 className="sender-verification-title">{title}</h3>}

      <div className="sender-verification-list">
        {visibleSteps.map((stepItem, index) => (
          <React.Fragment key={stepItem.id ?? index}>
            <div
              className={`sender-verification-card${
                index === visibleSteps.length - 1 && !allVisible
                  ? ' is-clickable'
                  : ''
              }`}
              onClick={
                index === visibleSteps.length - 1 && !allVisible
                  ? handleShowNext
                  : undefined
              }
            >
              <div className="sender-verification-card-step">
                <span className="sender-verification-card-step-num">
                  {stepItem.step}
                </span>
              </div>
              <div className="sender-verification-card-body">
                <div className="sender-verification-card-header">
                  {stepItem.step && iconMap[Number(stepItem.step)] && (
                    <div
                      className="sender-verification-card-icon"
                      dangerouslySetInnerHTML={{
                        __html: iconMap[Number(stepItem.step)] || '',
                      }}
                    />
                  )}
                  {stepItem.title && (
                    <h4 className="sender-verification-card-title">
                      {stepItem.title}
                    </h4>
                  )}
                </div>
                {stepItem.description && (
                  <p className="sender-verification-card-desc">
                    {stepItem.description}
                  </p>
                )}
              </div>
              {index === visibleSteps.length - 1 && !allVisible && (
                <div className="sender-verification-card-expand">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 5V19M12 19L5 12M12 19L19 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {/* <img
                    src={ImgPoint}
                    alt=""
                    className="sender-verification-card-expand-guide"
                  /> */}
                </div>
              )}
            </div>
            {index < visibleSteps.length - 1 && <DownArrow />}
          </React.Fragment>
        ))}
      </div>

      <div
        className="sender-verification-illustration"
        style={{ backgroundImage: `url(${imgBg || FourPartIllustration})` }}
      />
    </div>
  );
};

export default React.memo(SenderVerification);
